<template>
  <div class="detail-container">
    <div class="page-header no-print">
      <div class="breadcrumb">ນັກສຶກສາຕ່າງປະເທດ / <span>ປະຫວັດນັກສຶກສາ</span></div>
      <div class="header-content">
        <h1>ປະຫວັດນັກສຶກສາຮຽນຕ່າງປະເທດ</h1>
        <div style="display:flex;gap:10px;align-items:center;">
          <button class="btn-secondary" @click="$router.push('/home/students/oversea/list')">
            <i class="fas fa-arrow-left"></i> ກັບຄືນ
          </button>
          <button class="btn-primary" @click="doPrint" :disabled="loading || !student">
            <i class="fas fa-print"></i> ພິມເອກະສານ
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="state-panel no-print"><i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດຂໍ້ມູນ...</div>
    <div v-else-if="!student" class="state-panel no-print">ບໍ່ພົບຂໍ້ມູນນັກສຶກສາ</div>

    <div v-else class="document-shell">
      <div class="official-document" ref="printArea">
        <!-- ຫົວເອກະສານແບບລັດຖະການ -->
        <div class="doc-letterhead">
          <div class="letterhead-text">
            <h2>ສາທາລະນະລັດ ປະຊາທິປະໄຕ ປະຊາຊົນລາວ</h2>
            <div class="motto">ສັນຕິພາບ ເອກະລາດ ປະຊາທິປະໄຕ ເອກະພາບ ວັດທະນະຖາວອນ</div>
            <div class="motto-rule"></div>
          </div>
        </div>
        <div class="doc-ministry">ກະຊວງສຶກສາທິການ ແລະ ກິລາ</div>

        <div class="doc-title-block">
          <h1>ໃບປະຫວັດນັກສຶກສາຮຽນຕ່າງປະເທດ</h1>
          <div class="doc-subtitle">OVERSEAS STUDENT PROFILE RECORD</div>
        </div>

        <div class="doc-meta-row">
          <span>ເລກທີ (ລະຫັດນັກສຶກສາ): <b>{{ student.student_code }}</b></span>
          <span v-if="student.created_at">ວັນທີບັນທຶກຂໍ້ມູນ: <b>{{ formatDate(student.created_at) }}</b></span>
          <span>ພິມວັນທີ: <b>{{ todayFormatted }}</b></span>
        </div>

        <div class="doc-body">
          <div class="doc-photo-col">
            <div class="photo-frame">
              <img v-if="student.current_photo_base64" :src="student.current_photo_base64" alt="photo">
              <div v-else class="photo-placeholder"><i class="fas fa-user"></i></div>
            </div>
            <div class="status-badges">
              <span class="badge" :class="badgeClass(student.student_status)">{{ statusLabel(student.student_status) }}</span>
            </div>
          </div>

          <div class="doc-info-col">
            <section class="doc-section">
              <h3 class="section-title">I. ຂໍ້ມູນສ່ວນຕົວ</h3>
              <div class="info-grid">
                <div class="info-item"><label>ຊື່ (ພາສາລາວ)</label><span>{{ displayFirstNameLao }}</span></div>
                <div class="info-item"><label>ນາມສະກຸນ (ພາສາລາວ)</label><span>{{ displayLastNameLao }}</span></div>
                <div class="info-item"><label>ຊື່ (ພາສາອັງກິດ)</label><span>{{ displayFirstNameEng }}</span></div>
                <div class="info-item"><label>ນາມສະກຸນ (ພາສາອັງກິດ)</label><span>{{ displayLastNameEng }}</span></div>
                <div class="info-item"><label>ເພດ</label><span>{{ student.gender || '-' }}</span></div>
                <div class="info-item"><label>ວັນເດືອນປີເກີດ</label><span>{{ formatDate(student.date_of_birth) }}</span></div>
                <div class="info-item"><label>ເລກໜັງສືຜ່ານແດນ</label><span>{{ student.passport_number || '-' }}</span></div>
                <div class="info-item"><label>ລະຫັດນັກສຶກສາ</label><span>{{ student.student_code || '-' }}</span></div>
              </div>
            </section>

            <section class="doc-section">
              <h3 class="section-title">II. ທີ່ຢູ່ບ້ານເກີດ ແລະ ຜູ້ຕິດຕໍ່ສຸກເສີນ</h3>
              <div class="info-grid">
                <div class="info-item"><label>ແຂວງເກີດ</label><span>{{ locationNames.home_province || '-' }}</span></div>
                <div class="info-item"><label>ເມືອງເກີດ</label><span>{{ locationNames.home_district || '-' }}</span></div>
                <div class="info-item"><label>ຕາແສງເກີດ</label><span>{{ student.home_commune || '-' }}</span></div>
                <div class="info-item"><label>ບ້ານເກີດ</label><span>{{ student.home_village || '-' }}</span></div>
                <div class="info-item"><label>ຜູ້ຕິດຕໍ່ສຸກເສີນ</label><span>{{ student.home_emergency_contact_name || '-' }}</span></div>
                <div class="info-item"><label>ສາຍພົວພັນ</label><span>{{ student.home_emergency_contact_relation || '-' }}</span></div>
                <div class="info-item"><label>ເບີໂທ / ອີເມວ</label><span>{{ student.home_emergency_contact_number || '-' }} / {{ student.home_emergency_contact_email || '-' }}</span></div>
                <div class="info-item full"><label>ຂໍ້ມູນອື່ນໆ</label><span>{{ student.home_emergency_contact_note || '-' }}</span></div>
              </div>
            </section>

            <section class="doc-section">
              <h3 class="section-title">III. ຂໍ້ມູນຕິດຕໍ່ປັດຈຸບັນ</h3>
              <div class="info-grid">
                <div class="info-item"><label>ເບີຕິດຕໍ່ຢູ່ລາວ</label><span>{{ student.current_home_number || '-' }}</span></div>
                <div class="info-item"><label>ເບີຕິດຕໍ່ຕ່າງປະເທດ</label><span>{{ student.current_contact_number || '-' }}</span></div>
                <div class="info-item"><label>ອີເມວຕິດຕໍ່</label><span>{{ student.current_contact_email || '-' }}</span></div>
                <div class="info-item"><label>ຊ່ອງທາງອື່ນໆ</label><span>{{ student.current_other_contact || '-' }}</span></div>
                <div class="info-item full"><label>ທີ່ຢູ່ປັດຈຸບັນ (ພາສາທ້ອງຖິ່ນ)</label><span>{{ student.current_address_local || '-' }}</span></div>
                <div class="info-item full"><label>ທີ່ຢູ່ປັດຈຸບັນ (ພາສາອັງກິດ)</label><span>{{ student.current_address_eng || '-' }}</span></div>
              </div>
            </section>

            <section class="doc-section">
              <h3 class="section-title">IV. ລາຍລະອຽດຫຼັກສູດ</h3>
              <div class="info-grid">
                <div class="info-item"><label>ປະເທດທີ່ໄປສຶກສາ</label><span>{{ student.host_country || '-' }}</span></div>
                <div class="info-item"><label>ລະດັບການສຶກສາ</label><span>{{ student.degree_level || '-' }}</span></div>
                <div class="info-item"><label>ຊື່ສະຖາບັນການສຶກສາ</label><span>{{ student.university_name || '-' }}</span></div>
                <div class="info-item"><label>ຊື່ສະຖາບັນ (ພາສາອັງກິດ)</label><span>{{ student.university_name_eng || '-' }}</span></div>
                <div class="info-item"><label>ຊື່ສະຖາບັນ (ພາສາທ້ອງຖິ່ນ)</label><span>{{ student.university_name_local || '-' }}</span></div>
                <div class="info-item"><label>ສະຖານທີ່ຮຽນ (Campus)</label><span>{{ student.campus_name || '-' }}</span></div>
                <div class="info-item"><label>ສາຂາວິຊາ</label><span>{{ student.field_of_study || '-' }}</span></div>
                <div class="info-item"><label>ຊື່ຫຼັກສູດ</label><span>{{ student.course_name || '-' }}</span></div>
                <div class="info-item"><label>ຄະນະ</label><span>{{ student.department_school_name || '-' }}</span></div>
                <div class="info-item"><label>ເບີໂທຕິດຕໍ່ຄະນະ</label><span>{{ student.department_contact_number || '-' }}</span></div>
                <div class="info-item"><label>ອີເມວຕິດຕໍ່ຄະນະ</label><span>{{ student.department_contact_email || '-' }}</span></div>
                <div class="info-item"><label>ວິຊາສະເພາະ</label><span>{{ student.major_detail || '-' }}</span></div>
                <div class="info-item"><label>ສົກຮຽນເລີ່ມຕົ້ນ</label><span>{{ student.academic_year_start || '-' }}</span></div>
                <div class="info-item"><label>ຄາດວ່າຈະຈົບ</label><span>{{ student.expected_graduation || '-' }}</span></div>
              </div>
            </section>

            <section class="doc-section">
              <h3 class="section-title">V. ທຶນການສຶກສາ</h3>
              <div class="info-grid">
                <div class="info-item"><label>ປະເພດທຶນການສຶກສາ</label><span>{{ student.funding_type || '-' }}</span></div>
                <div class="info-item"><label>ຜູ້ສະໜັບສະໜູນ</label><span>{{ student.sponsor_details || '-' }}</span></div>
              </div>
            </section>

            <section class="doc-section" v-if="student.comments">
              <h3 class="section-title">VI. ໝາຍເຫດ</h3>
              <p class="comments-text">{{ student.comments }}</p>
            </section>
          </div>
        </div>

        <section class="doc-section doc-attachments">
          <h3 class="section-title">VII. ເອກະສານແນບ ({{ allDocuments.length }})</h3>
          <div v-if="allDocuments.length === 0" class="empty-docs">ບໍ່ມີເອກະສານແນບ</div>
          <ul v-else class="doc-list">
            <li v-for="(doc, i) in allDocuments" :key="i" class="doc-item">
              <i class="fas" :class="fileIcon(doc.name)"></i>
              <span class="doc-name">{{ doc.name }}</span>
              <span class="doc-actions no-print">
                <button type="button" @click="previewDoc(doc)"><i class="fas fa-eye"></i> ເບິ່ງ</button>
                <a :href="doc.data" :download="doc.name"><i class="fas fa-download"></i> ດາວໂຫລດ</a>
              </span>
            </li>
          </ul>
        </section>

        <div class="doc-signature-row">
          <div class="sig-box">
            <div class="sig-label">ຜູ້ບັນທຶກຂໍ້ມູນ</div>
            <div class="sig-line"></div>
            <div class="sig-name">{{ student.created_by || '...........................' }}</div>
          </div>
          <div class="sig-box">
            <div class="sig-label">ຢັ້ງຢືນໂດຍ (ລາຍເຊັນ ແລະ ປະທັບກາ)</div>
            <div class="sig-line"></div>
            <div class="sig-date">ວັນທີ: ...... /...... /............</div>
          </div>
        </div>

        <div class="doc-footer-note">ເອກະສານສະບັບນີ້ພິມຈາກລະບົບຄຸ້ມຄອງຂໍ້ມູນນັກສຶກສາ (SLFIS-KMS) ວັນທີ {{ todayFormatted }}</div>
      </div>
    </div>

    <!-- Modal ເບິ່ງເອກະສານ -->
    <transition name="modal">
      <div v-if="previewModal.show" class="modal-overlay no-print" @mousedown.self="closePreview">
        <div class="preview-modal-card">
          <div class="preview-header">
            <h3>{{ previewModal.name }}</h3>
            <button type="button" class="close-btn" @click="closePreview">&times;</button>
          </div>
          <div class="preview-body">
            <img v-if="previewModal.isImage" :src="previewModal.data" :alt="previewModal.name">
            <iframe v-else-if="previewModal.isPdf" :src="previewModal.data" title="preview"></iframe>
            <div v-else class="no-preview">
              <i class="fas fa-file-alt"></i>
              <p>ບໍ່ສາມາດສະແດງຕົວຢ່າງໄຟລ໌ປະເພດນີ້ໄດ້</p>
              <a :href="previewModal.data" :download="previewModal.name" class="btn-primary">ດາວໂຫລດເອກະສານ</a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';
import { studentStatusLabel } from '@/utils/overseaStatus';

export default {
  name: 'OverseaStudentDetailPage',
  props: { id: { type: String, default: null } },
  data() {
    return {
      loading: true,
      student: null,
      user: {},
      locationNames: { home_province: '', home_district: '' },
      previewModal: { show: false, name: '', data: '', isImage: false, isPdf: false }
    };
  },
  computed: {
    allDocuments() {
      if (!this.student) return [];
      return [...(this.student.moes_agreement_files || []), ...(this.student.supporting_docs || [])];
    },
    displayFirstNameLao() {
      if (!this.student) return '-';
      if (this.student.first_name_lao) return this.student.first_name_lao;
      return (this.student.full_name_lao || '').trim().split(/\s+/)[0] || '-';
    },
    displayLastNameLao() {
      if (!this.student) return '-';
      if (this.student.last_name_lao) return this.student.last_name_lao;
      return (this.student.full_name_lao || '').trim().split(/\s+/).slice(1).join(' ') || '-';
    },
    displayFirstNameEng() {
      if (!this.student) return '-';
      if (this.student.first_name_eng) return this.student.first_name_eng;
      return (this.student.full_name_eng || '').trim().split(/\s+/)[0] || '-';
    },
    displayLastNameEng() {
      if (!this.student) return '-';
      if (this.student.last_name_eng) return this.student.last_name_eng;
      return (this.student.full_name_eng || '').trim().split(/\s+/).slice(1).join(' ') || '-';
    },
    todayFormatted() {
      return new Date().toLocaleDateString('lo-LA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
  },
  mounted() {
    this.loadUserInfo();
    this.fetchData();
  },
  methods: {
    loadUserInfo() {
      const savedInfo = localStorage.getItem('user-info');
      if (savedInfo) {
        try { this.user = JSON.parse(savedInfo); } catch (e) { this.user = {}; }
      }
    },
    async fetchData() {
      const studentId = this.id || this.$route.params.id;
      this.loading = true;
      try {
        const res = await api.get(`/get/oversea_students/${studentId}`);
        if (res.data.code === 1 && res.data.rows) {
          if (this.user.org_role === 'consulate' && this.user.country && res.data.rows.host_country !== this.user.country) {
            this.$router.replace('/forbidden');
            return;
          }
          this.student = res.data.rows;
          this.populateLocationNames();
        } else {
          this.student = null;
        }
      } catch (e) {
        console.error('fetchData error:', e);
        this.student = null;
      } finally {
        this.loading = false;
      }
    },
    async populateLocationNames() {
      try {
        if (this.student.home_prov_id) {
          const prov = await api.get('/getItems/province/prov_name/prov_id');
          const found = (prov.data || []).find(i => i.value == this.student.home_prov_id);
          if (found) this.locationNames.home_province = found.text;
        }
        if (this.student.home_district_id && this.student.home_prov_id) {
          const dist = await api.get(`/getItemsCondition/district/district_name_lao/district_id/prov_id/${this.student.home_prov_id}`);
          const found = (dist.data || []).find(i => i.value == this.student.home_district_id);
          if (found) this.locationNames.home_district = found.text;
        }
      } catch (e) {
        console.error('populateLocationNames error:', e);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('lo-LA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
    statusLabel(status) {
      return studentStatusLabel(status);
    },
    badgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s.includes('active') || s.includes('approved') || s.includes('verified') || s.includes('graduated')) return 'badge-success';
      if (s.includes('pending')) return 'badge-warning';
      if (s.includes('reject') || s.includes('inactive') || s.includes('suspend') || s.includes('withdrawn')) return 'badge-danger';
      return 'badge-neutral';
    },
    fileIcon(name) {
      const ext = (name || '').split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fa-file-image';
      if (ext === 'pdf') return 'fa-file-pdf';
      if (['doc', 'docx'].includes(ext)) return 'fa-file-word';
      if (['xls', 'xlsx'].includes(ext)) return 'fa-file-excel';
      return 'fa-file-alt';
    },
    previewDoc(doc) {
      const mime = (doc.data || '').substring(5, (doc.data || '').indexOf(';'));
      this.previewModal = {
        show: true,
        name: doc.name,
        data: doc.data,
        isImage: mime.startsWith('image/'),
        isPdf: mime === 'application/pdf'
      };
    },
    closePreview() {
      this.previewModal.show = false;
    },
    doPrint() {
      window.print();
    }
  }
};
</script>

<style scoped>
.detail-container { animation: fadeIn 0.4s ease; }
.header-content { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
.btn-primary, .btn-secondary {
  border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;
  font-weight: 500; display: flex; align-items: center; gap: 8px; font-family: inherit;
}
.btn-primary { background: var(--lao); color: #fff; }
.btn-primary:hover { background: var(--lao-mid); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #fff; color: var(--text2); border: 1px solid var(--border); }
.btn-secondary:hover { background: #f4f5f7; }

.state-panel {
  background: var(--card); border: 1px solid var(--border); border-radius: 10px;
  padding: 60px 20px; text-align: center; color: var(--text3); margin-top: 16px;
}

.document-shell { display: flex; justify-content: center; margin-top: 16px; }

/* ==== ເອກະສານແບບລັດຖະການ ==== */
.official-document {
  background: #fff;
  width: 100%;
  max-width: 860px;
  padding: 40px 48px;
  border: 1px solid var(--border);
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border-radius: 4px;
}
.doc-letterhead { display: flex; align-items: center; justify-content: center; text-align: center; }
.letterhead-text h2 { font-size: 16px; font-weight: 700; color: var(--text1); }
.letterhead-text .motto { font-size: 12.5px; color: var(--text2); margin-top: 2px; }
.motto-rule { width: 140px; height: 1px; background: var(--text2); margin: 4px auto 0; }
.doc-ministry { text-align: center; font-size: 13.5px; font-weight: 600; color: var(--lao); margin-top: 10px; }

.doc-title-block { text-align: center; margin: 22px 0 10px; }
.doc-title-block h1 { font-size: 20px; font-weight: 700; color: var(--text1); letter-spacing: 0.02em; }
.doc-subtitle { font-size: 11px; letter-spacing: 0.12em; color: var(--gold); font-weight: 600; margin-top: 4px; }

.doc-meta-row {
  display: flex; justify-content: space-between; font-size: 12.5px; color: var(--text2);
  border-top: 2px solid var(--gold); border-bottom: 1px solid var(--border);
  padding: 8px 2px; margin-bottom: 20px;
}

.doc-body { display: flex; gap: 28px; align-items: flex-start; }
.doc-photo-col { flex: 0 0 130px; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.photo-frame {
  width: 120px; height: 150px; border: 2px solid var(--lao); border-radius: 4px;
  overflow: hidden; background: var(--surface); display: flex; align-items: center; justify-content: center;
}
.photo-frame img { width: 100%; height: 100%; object-fit: cover; }
.photo-placeholder { color: var(--text3); font-size: 40px; }
.status-badges { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.badge {
  font-size: 11px; padding: 4px 8px; border-radius: 4px; text-align: center; font-weight: 600;
}
.badge-success { background: var(--success-bg); color: var(--success); }
.badge-warning { background: var(--warning-bg); color: var(--warning); }
.badge-danger { background: var(--danger-bg); color: var(--danger); }
.badge-neutral { background: var(--surface); color: var(--text2); }

.doc-info-col { flex: 1; min-width: 0; }
.doc-section { margin-bottom: 16px; }
.section-title {
  font-size: 13px; font-weight: 700; color: #fff; background: var(--lao);
  padding: 5px 10px; border-radius: 3px; margin-bottom: 8px;
}
.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 20px; }
.info-item { display: flex; flex-direction: column; font-size: 12.5px; border-bottom: 1px dotted var(--border); padding-bottom: 4px; }
.info-item.full { grid-column: 1 / -1; }
.info-item label { color: var(--text3); font-size: 11px; margin-bottom: 2px; }
.info-item span { color: var(--text1); font-weight: 500; }
.comments-text { font-size: 12.5px; color: var(--text1); line-height: 1.6; }

.doc-attachments { margin-top: 8px; }
.empty-docs { font-size: 12.5px; color: var(--text3); padding: 8px 0; }
.doc-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
.doc-item {
  display: flex; align-items: center; gap: 10px; font-size: 12.5px;
  padding: 8px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--surface);
}
.doc-item i.fa-file-pdf { color: var(--danger); }
.doc-item i.fa-file-image { color: var(--lao-mid); }
.doc-item i.fa-file-word { color: #2b579a; }
.doc-item i.fa-file-excel { color: var(--success); }
.doc-name { flex: 1; word-break: break-all; }
.doc-actions { display: flex; gap: 6px; }
.doc-actions button, .doc-actions a {
  border: 1px solid var(--border); background: #fff; color: var(--lao); font-size: 11px;
  padding: 4px 8px; border-radius: 4px; cursor: pointer; text-decoration: none; font-family: inherit;
}
.doc-actions button:hover, .doc-actions a:hover { background: var(--lao-light); }

.doc-signature-row { display: flex; justify-content: space-between; margin-top: 40px; gap: 40px; }
.sig-box { flex: 1; text-align: center; }
.sig-label { font-size: 12px; color: var(--text2); margin-bottom: 40px; }
.sig-line { border-top: 1px dashed var(--text3); width: 80%; margin: 0 auto; }
.sig-name, .sig-date { font-size: 12px; color: var(--text1); margin-top: 6px; font-weight: 600; }

.doc-footer-note { margin-top: 24px; text-align: center; font-size: 10.5px; color: var(--text3); }

/* ==== Modal ເບິ່ງເອກະສານ ==== */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(13,27,42,0.55); display: flex;
  align-items: center; justify-content: center; z-index: 1000; padding: 20px;
}
.preview-modal-card {
  background: #fff; border-radius: 10px; width: 100%; max-width: 720px; max-height: 88vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.preview-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid var(--border); }
.preview-header h3 { font-size: 14px; color: var(--text1); word-break: break-all; }
.close-btn { border: none; background: none; font-size: 22px; cursor: pointer; color: var(--text3); line-height: 1; }
.preview-body { padding: 16px; overflow: auto; display: flex; align-items: center; justify-content: center; flex: 1; background: var(--surface); }
.preview-body img { max-width: 100%; max-height: 70vh; object-fit: contain; }
.preview-body iframe { width: 100%; height: 70vh; border: none; background: #fff; }
.no-preview { text-align: center; color: var(--text2); }
.no-preview i { font-size: 40px; color: var(--text3); margin-bottom: 10px; }
.no-preview p { margin: 8px 0 14px; }

@media print {
  .no-print { display: none !important; }
  body * { visibility: hidden; }
  .official-document, .official-document * { visibility: visible; }
  .official-document {
    position: absolute; top: 0; left: 0; width: 100%; max-width: 100%;
    box-shadow: none; border: none; padding: 10mm;
  }
  .doc-actions { display: none !important; }
}
</style>
